import React, { FormEvent, useState, useEffect } from 'react';
import { getRoles } from '../../api/roles';
import { Modal, ModalHeader, Form, ModalBody, Row, Col, FormGroup, Label, Input, FormFeedback, ModalFooter, Button, Alert } from 'reactstrap';
import { createUsersForAccount, inviteSSOUser, SSOUserInviteResult } from '../../api/users';
import { toast } from 'react-toastify';
import { PermissionAssociation, Role } from '../../types/role';
import styles from '../../styles/archiveui.module.css';

interface UserInviteModalProps {
  modal: boolean;
  toggle: () => void;
  accountId: string;
  fetchUsers?: () => Promise<void>;
}

type FormError = { email: string; roleId: string; externalUserId: string };

const UserInviteModal = ({ modal, toggle, accountId, fetchUsers }: UserInviteModalProps) => {
  const permissionsAssociations: PermissionAssociation[] = [];
  const [roles, updateRoles] = useState<Role[]>([]);
  const [invitedUser, setInvitedUser] = useState({
    invitedUserEmail: '',
    invitedUserRoleId: '',
    invitedUserRole: {
      id: '',
      accountId: '',
      name: '',
      description: '',
      created: new Date(),
      modified: new Date(),
      permissions: permissionsAssociations,
    },
  });
  const [formErrors, setFormErrors] = useState<FormError>({ email: '', roleId: '', externalUserId: '' });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSSOUser, setIsSSOUser] = useState(false);
  const [externalUserId, setExternalUserId] = useState('');
  const [existingUserToConfirm, setExistingUserToConfirm] = useState<SSOUserInviteResult['existingUser'] | null>(null);

  const addToInvitedUser = (key: string, value: any) => {
    setInvitedUser((prevValues) => {
      return { ...prevValues, [key]: value };
    });
  };

  useEffect(() => {
    const setRoles = async () => {
      const roles = await getRoles(accountId);
      updateRoles(roles);
    };
    setRoles();
  }, []);

  const resetForm = () => {
    setIsSSOUser(false);
    setExternalUserId('');
    setExistingUserToConfirm(null);
    setInvitedUser((prev) => ({ ...prev, invitedUserEmail: '', invitedUserRoleId: '' }));
    setFormErrors({ email: '', roleId: '', externalUserId: '' });
  };

  const handleToggle = () => {
    resetForm();
    toggle();
  };

  const validate = (email: string, roleId: string, extUserId: string): boolean => {
    const errors: FormError = { email: '', roleId: '', externalUserId: '' };
    let valid = true;

    if (!email.trim()) {
      errors.email = 'Email is required';
      valid = false;
    } else if (
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
        email.trim(),
      ) === false
    ) {
      errors.email = 'Email provided is not a valid email';
      valid = false;
    }

    if (isSSOUser && !extUserId.trim()) {
      errors.externalUserId = 'External User ID is required';
      valid = false;
    }

    if (roleId.trim().length < 1) {
      errors.roleId = 'You must select a role';
      valid = false;
    }

    setFormErrors(errors);
    return valid;
  };

  const _inviteRegularUserHandler = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validate(invitedUser.invitedUserEmail, invitedUser.invitedUserRoleId, '')) {
      return;
    }
    await toast.promise(createUsersForAccount(accountId, { email: invitedUser.invitedUserEmail }, [invitedUser.invitedUserRoleId]), {
      pending: 'Inviting User',
      success: 'User Invited',
      error: 'We were unable to invite this user',
    });
    if (fetchUsers) await fetchUsers();
    handleToggle();
  };

  const callInviteSSOUser = async (confirm = false) => {
    const result = await inviteSSOUser(accountId, {
      externalUserId: externalUserId.trim(),
      roleId: invitedUser.invitedUserRoleId,
      email: invitedUser.invitedUserEmail.trim(),
      confirm,
    });

    if (result.status === 'EXISTS_IN_OTHER_ACCOUNT') {
      setExistingUserToConfirm(result.existingUser ?? null);
      return;
    }

    if (result.status === 'ALREADY_IN_ACCOUNT') {
      toast.error('This user is already a member of this account.');
      return;
    }

    if (result.status === 'CREATED') {
      toast.success('SSO user created and added to account.');
    } else if (result.status === 'ADDED_TO_ACCOUNT') {
      toast.success('Existing SSO user added to this account.');
    }

    if (fetchUsers) await fetchUsers();
    setExistingUserToConfirm(null);
    handleToggle();
  };

  const _inviteSSOUserHandler = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validate(invitedUser.invitedUserEmail, invitedUser.invitedUserRoleId, externalUserId)) {
      return;
    }
    await callInviteSSOUser();
  };

  const submitHandler = async (event: FormEvent<HTMLFormElement>) => {
    setIsSubmitting(true);
    try {
      if (isSSOUser) {
        await _inviteSSOUserHandler(event);
      } else {
        await _inviteRegularUserHandler(event);
      }
    } catch (e) {
      toast.error('Unable to invite this user, please try again.');
      console.log(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmAddExistingUser = async () => {
    setIsSubmitting(true);
    try {
      await callInviteSSOUser(true);
    } catch (e) {
      toast.error('Unable to add user, please try again.');
      console.log(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const existingUserDisplayName = existingUserToConfirm
    ? [existingUserToConfirm.givenName, existingUserToConfirm.familyName].filter(Boolean).join(' ') || existingUserToConfirm.email || 'Unknown'
    : '';

  return (
    <div>
      <Modal toggle={handleToggle} isOpen={modal} style={{ minWidth: '40em' }}>
        <ModalHeader style={{ backgroundColor: '#ceeaf4' }} toggle={handleToggle}>
          Invite User
        </ModalHeader>

        {existingUserToConfirm ? (
          <>
            <ModalBody style={{ backgroundColor: '#F4F4F4' }}>
              <Alert color="warning">
                <strong>User already exists in the system</strong>
                <p className="mb-1 mt-2">
                  An SSO user with External ID <code>{externalUserId}</code> already exists:
                </p>
                <ul className="mb-0">
                  {existingUserDisplayName && <li>Name: {existingUserDisplayName}</li>}
                  {existingUserToConfirm.email && <li>Email: {existingUserToConfirm.email}</li>}
                </ul>
                <p className="mb-0 mt-2">Would you like to add this user to the current account?</p>
              </Alert>
            </ModalBody>
            <ModalFooter style={{ backgroundColor: '#F4F4F4' }}>
              <Button className={styles.scrbBtnBlue} disabled={isSubmitting} onClick={() => setExistingUserToConfirm(null)}>
                Cancel
              </Button>
              <Button className={styles.scrbBtnOrange} disabled={isSubmitting} onClick={confirmAddExistingUser}>
                Add to Account
              </Button>
            </ModalFooter>
          </>
        ) : (
          <Form onSubmit={submitHandler}>
            <ModalBody style={{ backgroundColor: '#F4F4F4' }}>
              <Row form>
                <Col>
                  <FormGroup check className="mb-3">
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={isSSOUser}
                        onChange={(e) => {
                          setIsSSOUser(e.target.checked);
                          setExternalUserId('');
                          setExistingUserToConfirm(null);
                          setFormErrors({ email: '', roleId: '', externalUserId: '' });
                        }}
                      />{' '}
                      SSO User (Federated)
                    </Label>
                  </FormGroup>
                </Col>
              </Row>

              {isSSOUser && (
                <Row form>
                  <Col>
                    <FormGroup>
                      <Label for="externalUserId">External User ID</Label>
                      <Input
                        id="externalUserId"
                        required
                        invalid={formErrors.externalUserId !== ''}
                        placeholder="e.g. auth0|abc123"
                        value={externalUserId}
                        onChange={(e) => setExternalUserId(e.target.value)}
                      />
                      <FormFeedback>{formErrors.externalUserId}</FormFeedback>
                    </FormGroup>
                  </Col>
                </Row>
              )}

              <Row form>
                <Col>
                  <FormGroup>
                    <Label for="email">Email</Label>
                    <Input
                      id="email"
                      required
                      invalid={formErrors.email !== ''}
                      value={invitedUser.invitedUserEmail}
                      onChange={(e) => {
                        addToInvitedUser('invitedUserEmail', e.target.value);
                      }}
                    />
                    <FormFeedback>{formErrors.email}</FormFeedback>
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col>
                  <FormGroup>
                    <Label for="role">Role</Label>
                    <Input
                      id="role"
                      required
                      invalid={formErrors.roleId !== ''}
                      type="select"
                      onChange={(e) => {
                        addToInvitedUser('invitedUserRoleId', e.target.value);
                      }}
                    >
                      <option></option>
                      {roles.map((role, key) => {
                        return (
                          <option key={key} value={role.id}>
                            {role.name}
                          </option>
                        );
                      })}
                    </Input>
                    <FormFeedback>{formErrors.roleId}</FormFeedback>
                  </FormGroup>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter style={{ backgroundColor: '#F4F4F4' }}>
              <Button className={styles.scrbBtnBlue} onClick={handleToggle}>
                Cancel
              </Button>{' '}
              <Button type="submit" disabled={isSubmitting} className={styles.scrbBtnOrange}>
                {isSSOUser ? 'Add SSO User' : 'Send Invitation'}
              </Button>
            </ModalFooter>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default UserInviteModal;
